import { useEffect, useState } from 'preact/hooks';
import { api } from '@/lib/teaching/teacher-api';
import { useAssessmentId, invalidateDataset, refreshAssessments, assessments } from '@/lib/teaching/state';
import { errText } from '@/lib/teaching/slice2';
import { FINALISED_TEXT, type QcRow, type QcStatus, type QcView } from '@/lib/teaching/slice4';
import { TeachingPage, Card, AssessmentSwitcher, INK } from '@/components/teaching/ui';
import { KpiRow, KpiCard } from '@/components/teaching/kit';
import { Btn, DownloadSpreadsheet, ErrorLine, inputCls, val } from '@/components/teaching/form';
import { Tabs } from '@/components/teaching/setup';

// Checkpoints, QC audit, finalise and reopen (slice 4 §06; U1, U5, U11,
// U13). QC is computed live by the server; a failing row blocks passing.

const STATUS_LOOK: Record<QcStatus, { label: string; bg: string }> = {
  pass: { label: 'Pass', bg: '#cdeed4' },
  fail: { label: 'Fail', bg: '#f8cdcd' },
  not_applicable: { label: 'Not applicable', bg: '#e2e3e6' },
};

const BANNER = {
  provisional: { text: 'Not final yet', sub: 'Checkpoint 2 has not been passed, so numbers may still change', bg: '#fff6e0' },
  checkpoint_2: { text: 'Checkpoint 2 passed', sub: 'Main class checked', bg: '#e8effb' },
  finalised: { text: 'Finalised', sub: 'Locked. Reopen with a reason to change anything', bg: '#d3f0d9' },
} as const;

// Plain names for checks whose built-in label is internal wording.
const PLAIN_LABEL: Record<string, string> = {
  map: 'Questions and mastery marks', scan_reconciliation: 'Scans', identification: 'How papers were matched to students',
  unmatched: 'Unmatched or missing papers', duplicate: 'Duplicates', myp: 'MYP levels', visual: 'Colours and blank cells', class_status: 'Every student accounted for',
};
const plainDetail = (d: string) => d.replace(/\bitems\b/g, 'questions').replace(/\bitem\b/g, 'question').replace('Manual entry', 'Marks entered by hand').replace('Verified by build tests', 'Checked automatically');

function QcRowView({ r, onManual, finalised }: { r: QcRow; onManual: (ids: string[]) => void; finalised: boolean }) {
  const [open, setOpen] = useState(false);
  const look = STATUS_LOOK[r.status];
  const n = r.students?.length ?? 0;
  return (
    <tr class="border-t border-[var(--color-border)] align-top" data-qc-row={r.id} data-qc-status={r.status}>
      <td class="sticky left-0 z-10 bg-[var(--color-card)] px-2 py-2 font-medium text-[var(--color-text)] min-w-[130px]">{PLAIN_LABEL[r.id] ?? r.label}</td>
      <td class="px-2 py-2"><span class="text-[10.5px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap" style={{ background: look.bg, color: INK }}>{look.label}</span></td>
      <td class="px-2 py-2 text-[var(--color-text-muted)] min-w-[260px]">
        <div>{plainDetail(r.detail)}</div>
        {n > 0 && (
          <div class="mt-1">
            <button type="button" class="text-[11.5px] underline text-[var(--color-text-muted)]" onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'} {n} student{n === 1 ? '' : 's'}</button>
            {open && <div class="text-[11.5px] text-[var(--color-text-faint)] mt-0.5">{r.students!.map((s) => s.name).join(', ')}</div>}
          </div>
        )}
        {r.action?.kind === 'record_manual_entry' && r.action.studentIds.length > 0 && (
          <div class="mt-1.5">
            <Btn disabled={finalised} title={finalised ? FINALISED_TEXT : undefined} onClick={() => onManual(r.action!.studentIds)}>
              Record manual entry for these {r.action.studentIds.length}
            </Btn>
          </div>
        )}
      </td>
    </tr>
  );
}

export function Checkpoints() {
  const a = useAssessmentId();
  const [n, setN] = useState<1 | 2 | 3 | null>(null);
  const [v, setV] = useState<QcView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');

  async function load(which: 1 | 2 | 3 | null = n) {
    if (!a) return;
    let k = which;
    if (!k) {
      // Open on the next checkpoint to pass.
      const first = await api<QcView>('GET', `/api/t/assessments/${a}/qc?checkpoint=1`);
      const live = first.checkpoints.filter((c) => !c.stale).map((c) => c.n);
      k = first.assessment.status === 'finalised' || live.includes(2) ? 3 : live.includes(1) ? 2 : 1;
      setN(k);
      if (k === 1) { setV(first); return; }
    }
    setV(await api<QcView>('GET', `/api/t/assessments/${a}/qc?checkpoint=${k}`));
  }
  useEffect(() => { setV(null); setN(null); setErr(null); void load(null).catch((e) => setErr(errText(e))); }, [a]);

  async function act(fn: () => Promise<unknown>) {
    setErr(null); setBusy(true);
    try { await fn(); } catch (e) { setErr(errText(e)); }
    if (a) invalidateDataset(a);
    await refreshAssessments().catch(() => undefined);
    await load().catch((e) => setErr(errText(e)));
    setBusy(false);
  }
  const rev = () => v!.assessment.revision;
  const pass = () => act(() => api('POST', `/api/t/assessments/${a}/checkpoints`, { n, expectedRevision: rev() }));
  const finalise = () => act(() => api('POST', `/api/t/assessments/${a}/finalise`, { expectedRevision: rev() }));
  const reopen = () => act(async () => { await api('POST', `/api/t/assessments/${a}/reopen`, { expectedRevision: rev(), reason }); setReason(''); });
  const manual = (ids: string[]) => act(() => api('POST', `/api/t/assessments/${a}/scan-exceptions`, { studentIds: ids, reason: 'manual_entry' }));

  const t = a ? assessments.value?.find((x) => x.id === a) : null;
  const finalised = v?.assessment.status === 'finalised';
  const cp = (k: number) => v?.checkpoints.find((c) => c.n === k);
  const fails = v?.qc.rows.filter((r) => r.status === 'fail').length ?? 0;

  return (
    <TeachingPage title="Checkpoints" breadcrumb={t ? `${t.title} · Year ${t.year_level} · Criterion ${t.criterion}` : ''} actions={a && <><DownloadSpreadsheet a={a} /><AssessmentSwitcher current={a} /></>}>
      {a === null && <div class="text-[13px] text-[var(--color-text-muted)]">Create a test first.</div>}
      <ErrorLine error={err} />
      {a && !v && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {v && n && (
        <>
          <div class="rounded-xl px-3 py-2 text-[12.5px]" style={{ background: BANNER[v.banner].bg, color: INK }} data-qc-banner={v.banner}>
            <b>{BANNER[v.banner].text}.</b> {BANNER[v.banner].sub}.
          </div>
          <KpiRow>
            <KpiCard value={`${[1, 2, 3].filter((k) => cp(k) && !cp(k)!.stale).length}/3`} label="Checkpoints passed" />
            <KpiCard value={fails} label="Failing checks" tone={fails ? '#b42318' : undefined} hint={`checkpoint ${n}`} />
            <KpiCard value={v.qc.rows.filter((r) => r.status === 'pass').length} label="Checks passing" hint={`of ${v.qc.rows.length}`} />
            <KpiCard value={finalised ? 'Locked' : 'Open'} label="Test status" hint={finalised ? 'finalised' : 'you can still edit'} />
          </KpiRow>

          <Card title="Checkpoints passed">
            <div class="flex flex-col gap-1.5 text-[12.5px]">
              {[1, 2, 3].map((k) => {
                const c = cp(k);
                return (
                  <div key={k} class="flex flex-wrap items-baseline gap-x-2" data-checkpoint={k}>
                    <b class="text-[var(--color-text)] w-[96px]">Checkpoint {k}</b>
                    {c ? <span class="text-[var(--color-text-muted)]">passed by {c.passed_by}, {new Date(c.passed_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      : <span class="text-[var(--color-text-faint)]">not passed</span>}
                    {c?.stale && (
                      <span class="px-1.5 py-0.5 rounded text-[11.5px]" style={{ background: '#fddcbf', color: INK }} data-stale title={c.changed.students.map((s) => s.name).join(', ')}>{c.label}</span>
                    )}
                    {c?.stale && c.changed.students.length > 0 && <span class="text-[11px] text-[var(--color-text-faint)] basis-full pl-[104px]">{c.changed.students.map((s) => s.name).join(', ')}</span>}
                  </div>
                );
              })}
            </div>
          </Card>

          <div class="flex flex-wrap items-center gap-2">
            <Tabs label="Checkpoint" value={String(n) as '1' | '2' | '3'} onChange={(k) => { const x = Number(k) as 1 | 2 | 3; setN(x); void load(x); }}
              options={[{ id: '1', label: 'Checkpoint 1: setup' }, { id: '2', label: 'Checkpoint 2: main class' }, { id: '3', label: 'Checkpoint 3: final' }]} />
          </div>

          {v.prerequisite && <div class="rounded-xl px-3 py-2 text-[12.5px]" style={{ background: '#fff6e0', color: INK }}>{v.prerequisite}.</div>}

          <Card title={`Checks for checkpoint ${n}`} sub={fails ? `${fails} check${fails === 1 ? '' : 's'} failing. A failing check blocks this checkpoint.` : 'Every check passes or does not apply.'}>
            <div class="overflow-x-auto">
              <table class="border-collapse text-[12.5px] w-full">
                <thead><tr class="text-left text-[var(--color-text-muted)]">
                  <th class="sticky left-0 z-10 bg-[var(--color-card)] px-2 py-1.5 font-medium">Check</th><th class="px-2 py-1.5 font-medium">Result</th><th class="px-2 py-1.5 font-medium">Details</th>
                </tr></thead>
                <tbody>{v.qc.rows.map((r) => <QcRowView key={r.id} r={r} onManual={manual} finalised={!!finalised} />)}</tbody>
              </table>
            </div>
            
          </Card>

          <Card title="Next step">
            <div class="flex flex-wrap items-center gap-2">
              {!finalised && (
                <Btn kind="primary" disabled={busy || !v.qc.passed || !!v.prerequisite} onClick={pass}>
                  {cp(n) ? `Pass checkpoint ${n} again` : `Pass checkpoint ${n}`}
                </Btn>
              )}
              {!finalised && n === 3 && <Btn kind="primary" disabled={busy || !cp(3) || cp(3)!.stale} onClick={finalise}>Finalise</Btn>}
              {finalised && <span class="text-[12.5px] text-[var(--color-text-muted)]">This test is finalised. Nothing on it can be changed until you reopen it.</span>}
            </div>
            {finalised && (
              <div class="flex flex-wrap items-center gap-2 mt-3">
                <input aria-label="Reason to reopen" class={inputCls + ' flex-1 min-w-[200px] max-w-[420px]'} value={reason} onInput={(e) => setReason(val(e))} placeholder="Why does this test need changing?" />
                <Btn kind="danger" disabled={busy || !reason.trim()} onClick={reopen}>Reopen</Btn>
              </div>
            )}
          </Card>
        </>
      )}
      <div class="h-6" />
    </TeachingPage>
  );
}
